'use client' // Indicate that this component should run on the client side

import * as React from 'react'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import {
  Lexend,
  Noto_Sans,
  Noto_Sans_Gujarati,
  Noto_Sans_Bengali,
  Noto_Sans_Telugu,
  Noto_Sans_Kannada,
  Noto_Sans_Tamil,
  Noto_Sans_Oriya,
  Noto_Sans_Malayalam,
  Noto_Sans_Devanagari,
} from 'next/font/google'
import { PRIMARY, SECONDARY, SUCCESS, TEXT_DARK } from '@/constants/colors'

const lexend = Lexend({
  weight: ['300', '400', '500', '700'],
  subsets: ['latin'],
  display: 'swap',
})

const notoSans = Noto_Sans({
  weight: ['400', '500', '600', '700', '900'],
  subsets: ['latin', 'devanagari'],
  display: 'swap',
})

const notoSansGujarati = Noto_Sans_Gujarati({
  weight: ['400', '500', '600', '700', '800', '900'],
  subsets: ['latin', 'gujarati'],
  display: 'swap',
})

const notoSansBengali = Noto_Sans_Bengali({
  weight: ['400', '500', '600', '700', '800', '900'],
  subsets: ['latin', 'bengali'],
  display: 'swap',
})

const notoSansTelugu = Noto_Sans_Telugu({
  weight: ['400', '500', '600', '700', '800', '900'],
  subsets: ['latin', 'telugu'],
  display: 'swap',
})

const notoSansKannada = Noto_Sans_Kannada({
  weight: ['400', '500', '600', '700', '800', '900'],
  subsets: ['latin', 'kannada'],
  display: 'swap',
})

const notoSansTamil = Noto_Sans_Tamil({
  weight: ['400', '500', '600', '700', '800', '900'],
  subsets: ['latin', 'tamil'],
  display: 'swap',
})

const notoSansOriya = Noto_Sans_Oriya({
  weight: ['400', '500', '600', '700', '800', '900'],
  subsets: ['latin', 'oriya'],
  display: 'swap',
})

const notoSansMalayalam = Noto_Sans_Malayalam({
  weight: ['400', '500', '600', '700', '800', '900'],
  subsets: ['latin', 'malayalam'],
  display: 'swap',
})

const notoSansDevanagari = Noto_Sans_Devanagari({
  weight: ['400', '500', '600', '700', '800', '900'],
  subsets: ['latin', 'devanagari'],
  display: 'swap',
})

// Function to determine the font based on the language
const getFontFamily = (lang: string) => {
  switch (lang) {
    case 'hi':
      return notoSans.style.fontFamily
    case 'mr':
      return notoSansDevanagari.style.fontFamily
    case 'gu':
      return notoSansGujarati.style.fontFamily
    case 'bn':
      return notoSansBengali.style.fontFamily
    case 'te':
      return notoSansTelugu.style.fontFamily
    case 'kn':
      return notoSansKannada.style.fontFamily
    case 'ta':
      return notoSansTamil.style.fontFamily
    case 'or':
      return notoSansOriya.style.fontFamily
    case 'ml':
      return notoSansMalayalam.style.fontFamily
    default:
      return lexend.style.fontFamily
  }
}

const ClientThemeProvider = ({ children, locale }: { children: React.ReactNode; locale: string }) => {
  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode: 'light',
          primary: {
            main: PRIMARY,
          },
          secondary: {
            main: SECONDARY,
          },
          success: {
            main: SUCCESS,
          },
        },
        typography: {
          fontFamily: getFontFamily(locale || 'en'),
          allVariants: {
            color: TEXT_DARK,
          },
        },
      }),
    [locale],
  )

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  )
}

export default ClientThemeProvider
